import React, { useState } from 'react';
import { Mail, Send, CheckCircle, Download, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import { submitContact, trackVcardDownload } from '../api';
import useSeo from '../hooks/useSeo';

const Contact = () => {
  useSeo({
    title: 'Contact — Motion Designer & Photographer in Mansfield, TX',
    description: 'Get in touch with Christian D Animator — motion design, animation, video editing, family & real estate photography in Mansfield, TX (DFW Metroplex).',
  });
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSaveContact = () => {
    const siteUrl = (typeof window !== 'undefined' && window.location?.origin) || '';
    const vcard = [
      'BEGIN:VCARD',
      'VERSION:3.0',
      'FN:Christian D Animator',
      'N:Anderson;Christian;;;',
      'ORG:Christian D Animator',
      'TITLE:Motion Designer · Video Editor · Photographer',
      'EMAIL;TYPE=INTERNET,PREF:christiandanimator@gmail.com',
      siteUrl ? `URL:${siteUrl}` : '',
      'NOTE:Motion Design · Animation · Video Editing · Photography',
      'END:VCARD',
      '',
    ].filter(Boolean).join('\r\n');

    const blob = new Blob([vcard], { type: 'text/vcard;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'christian-d-animator.vcf';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);

    // Fire-and-forget analytics ping (never blocks the download)
    trackVcardDownload();

    toast({
      title: 'Contact saved',
      description: 'Open the downloaded file to add me to your contacts.',
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const res = await submitContact(formData);
      toast({
        title: 'Message Sent!',
        description: res.message || "Thanks for reaching out! I'll get back to you within 24 hours.",
      });
      setFormData({ name: '', email: '', service: '', message: '' });
    } catch (err) {
      toast({
        title: 'Something went wrong',
        description: err?.response?.data?.detail || 'Please try again or email me directly.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white pt-16">
      {/* Hero */}
      <section className="py-14 lg:py-20 bg-gradient-to-b from-blue-900/20 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="page-title text-4xl lg:text-6xl font-bold mb-6">
            <span className="bg-gradient-to-r from-blue-400 via-blue-400 to-lime-400 bg-clip-text text-transparent">Let's Work Together</span>
          </h1>
          <p className="text-lg sm:text-xl text-gray-300 max-w-3xl mx-auto">
            Have a project in mind? I'd love to hear about it. Fill out the form below or reach out directly.
          </p>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-14 lg:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Form */}
            <div>
              <h2 className="text-3xl font-bold mb-8">Send a Message</h2>
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div>
                  <Label htmlFor="name" className="text-gray-300 mb-2 block">Your Name</Label>
                  <Input
                    id="name"
                    name="name"
                    data-testid="contact-name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="John Doe"
                    required
                    className="bg-gray-900 border-gray-800 text-white focus:border-blue-400"
                  />
                </div>

                <div>
                  <Label htmlFor="email" className="text-gray-300 mb-2 block">Email Address</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    data-testid="contact-email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="john@example.com"
                    required
                    className="bg-gray-900 border-gray-800 text-white focus:border-blue-400"
                  />
                </div>

                <div>
                  <Label htmlFor="service" className="text-gray-300 mb-2 block">Service Needed</Label>
                  <select
                    id="service"
                    name="service"
                    data-testid="contact-service"
                    value={formData.service}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 bg-gray-900 border border-gray-800 rounded-md text-white focus:outline-none focus:border-blue-400"
                  >
                    <option value="">Select a service</option>
                    <option value="motion-design">Motion Design & Animation</option>
                    <option value="character-design">Character Design</option>
                    <option value="video-editing">Video Editing</option>
                    <option value="photography">Photography</option>
                    <option value="multiple">Multiple Services</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <Label htmlFor="message" className="text-gray-300 mb-2 block">Project Details</Label>
                  <Textarea
                    id="message"
                    name="message"
                    data-testid="contact-message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell me about your project..."
                    rows={6}
                    required
                    className="bg-gray-900 border-gray-800 text-white focus:border-blue-400 resize-none"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  data-testid="contact-submit"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-full py-6 text-lg"
                >
                  {isSubmitting ? (
                    'Sending...'
                  ) : (
                    <>
                      Send Message <Send className="ml-2" size={20} />
                    </>
                  )}
                </Button>
              </form>
            </div>

            {/* Contact Info */}
            <div>
              <h2 className="text-3xl font-bold mb-8">Get in Touch</h2>

              <Card className="bg-gray-900/50 border-gray-800 mb-6">
                <CardContent className="p-6">
                  <div className="flex items-start mb-4">
                    <Mail className="text-blue-400 mr-4 flex-shrink-0 mt-1" size={24} />
                    <div className="min-w-0 flex-1">
                      <h3 className="text-lg font-semibold mb-2">Email</h3>
                      <a
                        href="mailto:christiandanimator@gmail.com"
                        className="text-gray-400 hover:text-blue-400 transition-colors break-all"
                      >
                        christiandanimator@gmail.com
                      </a>
                      <Button
                        type="button"
                        onClick={handleSaveContact}
                        data-testid="save-contact-btn"
                        variant="outline"
                        className="mt-4 w-full sm:w-auto border-lime-400/60 text-lime-300 hover:bg-lime-400 hover:text-black hover:border-lime-400 rounded-full px-5 py-2 text-sm"
                      >
                        <Download className="mr-2" size={16} /> Save My Contact
                      </Button>
                      <p className="text-xs text-gray-500 mt-2">
                        Adds me to your phone's address book — one tap.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gray-900/50 border-gray-800 mb-6">
                <CardContent className="p-6">
                  <div className="flex items-start">
                    <MapPin className="text-lime-400 mr-4 flex-shrink-0 mt-1" size={24} />
                    <div>
                      <h3 className="text-lg font-semibold mb-2" data-testid="contact-service-area">Service Area</h3>
                      <p className="text-gray-400">
                        Based in <span className="text-white">Mansfield, TX</span>
                      </p>
                      <p className="text-gray-400 text-sm mt-1">
                        Serving the Dallas–Fort Worth Metroplex — Arlington, Fort Worth, Dallas, Grand Prairie, Burleson, Cedar Hill, Midlothian, and surrounding areas. Remote / virtual projects worldwide.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gray-900/50 border-gray-800 mb-6">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold mb-4">What to Expect</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <CheckCircle className="text-blue-400 mr-3 flex-shrink-0 mt-1" size={20} />
                      <span className="text-gray-400">Response within 24 hours</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="text-blue-400 mr-3 flex-shrink-0 mt-1" size={20} />
                      <span className="text-gray-400">Free initial consultation</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="text-blue-400 mr-3 flex-shrink-0 mt-1" size={20} />
                      <span className="text-gray-400">Detailed project quote</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="text-blue-400 mr-3 flex-shrink-0 mt-1" size={20} />
                      <span className="text-gray-400">Clear timeline and milestones</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <div className="bg-gradient-to-br from-blue-900/30 to-gray-900/30 rounded-lg p-6 border border-gray-800">
                <h3 className="text-xl font-semibold mb-3">Ready to Start?</h3>
                <p className="text-gray-400 mb-4">
                  Whether you need motion graphics, video editing, or photography — I'm here to help bring your vision to life.
                </p>
                <p className="text-sm text-gray-500">
                  Serving clients in marketing, tech, clean energy, aerospace, and corporate sectors.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;